import { useState } from "react";
import styled from "@emotion/styled";
import { Flex, Text } from "styles/utility-components";

import { LoadingState } from "components/app_layout/LoadingState";
import Button from "components/ui/button/Button";
import EmptyState from "components/ui/empty_state/EmptyState";
import TooltipComponent from "components/ui/tooltip/Tooltip";
import { CustomerDocumentDetails } from "lib/hooks/api/customer_documents/useCustomerDocuments";
import { useSubscription } from "lib/hooks/subscription";
import { usePaywall } from "lib/providers/Paywall";
import { BidResponseDrawer } from "../bid_response_drawer/BidResponseDrawer";
import { CreateBidResponsesModal } from "./modals/CreateBidResponsesModal";
import { BidResponseProcessingStatus } from "./BidResponsesProcessingProvider";

type Props = {
  bidId: string | undefined;
  isExtracting: boolean;
  isLoadingDocuments: boolean;
  status: BidResponseProcessingStatus;
  documents: CustomerDocumentDetails[];
};

export function EmptyBidResponses({
  bidId,
  isExtracting,
  isLoadingDocuments,
  status,
  documents,
}: Props): JSX.Element {
  if (isExtracting) {
    const totalDocs = documents?.length || 0;
    let processedDocs = 0;

    for (const doc in status) {
      if (status[doc].status === "completed" || status[doc].status === "error") {
        processedDocs += 1;
      }
    }

    return (
      <EmptyStateContainer isExtracting>
        <LoadingState
          title={`(${processedDocs}/${totalDocs}) Documents processed`}
          description="We're scanning your tender documents to extract the bid questions. This will take a
              couple of minutes. We will email you when it's ready - feel free to leave this page
              and come back later."
        />
      </EmptyStateContainer>
    );
  }

  return (
    <EmptyStateContainer>
      <EmptyState
        type="noResults"
        heading="Empty, but full of potential!"
        description="You haven't got any bid responses visible. Upload tender documents to extract the questions you need to answer or add them in manually"
        size="large"
        width="450px"
        yAxisMargin="64px"
        action={
          <Actions
            bidId={bidId}
            isExtracting={isExtracting}
            isLoadingDocuments={isLoadingDocuments}
          />
        }
      />
    </EmptyStateContainer>
  );
}

const EmptyStateContainer = styled(Flex)<{ isExtracting?: boolean }>(({ theme, isExtracting }) => ({
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  height: "100%",
  backgroundColor: isExtracting ? undefined : theme.colors.sysBackgroundAlternative,
}));

type ActionsProps = {
  bidId: string | undefined;
  isExtracting: boolean;
  isLoadingDocuments: boolean;
};
function Actions({ bidId, isExtracting, isLoadingDocuments }: ActionsProps): JSX.Element {
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isCreateResponsesModalOpen, setIsCreateResponsesModalOpen] = useState(false);
  const drawerDefaultValues = bidId ? { bidId } : {};

  const subscription = useSubscription();
  const { openPaywallModal } = usePaywall();

  const hasBidAccess = subscription.hasDataTypes("BIDS");

  return (
    <>
      <Flex column gap={8}>
        <TooltipComponent
          // Only show tooltip when there's no documents resource
          content={
            bidId ? undefined : (
              <Text white>Please upload documents before extracting questions</Text>
            )
          }
          side="right"
        >
          <Button
            onClick={() => {
              if (!hasBidAccess) {
                openPaywallModal("BID_RESPONSES");
                return;
              }

              setIsCreateResponsesModalOpen(true);
            }}
            disabled={!bidId || isLoadingDocuments}
            loading={isExtracting}
          >
            Extract Questions
          </Button>
        </TooltipComponent>

        <Button
          buttonType="secondary"
          onClick={() => {
            setIsCreateModalOpen(true);
          }}
        >
          Add question manually
        </Button>
      </Flex>

      <BidResponseDrawer
        type="create"
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onComplete={() => setIsCreateModalOpen(false)}
        defaultValues={drawerDefaultValues}
      />

      {bidId && (
        <CreateBidResponsesModal
          isOpen={isCreateResponsesModalOpen}
          onOpenChange={setIsCreateResponsesModalOpen}
          bidId={bidId}
        />
      )}
    </>
  );
}
